import { useEffect, useState } from 'react'
import { CheckCircle } from '../../components'
import { Button } from '../../components/elements/Button'
import { cn } from '../../utils'
import { useChatImage } from '../../hooks/useChatImage'
import { WandSparkles, CircleArrowDown, Info } from 'lucide-react'

// React-based rainbow glow component
const RainbowMagicpen = () => {
  const [colorIndex, setColorIndex] = useState(0)

  const rainbowColors = [
    '#ff0000', // Red
    '#ff7700', // Orange
    '#ffee00', // Yellow
    '#00ff00', // Green
    '#0077ff', // Blue
    '#7700ff', // Indigo
    '#ff00ff', // Violet
  ]

  useEffect(() => {
    const intervalId = setInterval(() => {
      setColorIndex((prevIndex) => (prevIndex + 1) % rainbowColors.length)
    }, 1000) // Change color every 1000ms

    return () => clearInterval(intervalId)
  }, [])

  const currentColor = rainbowColors[colorIndex]

  const iconStyle = {
    color: currentColor,
    filter: `drop-shadow(0 0 3px ${currentColor})`,
    transition: 'color 0.5s, filter 0.5s',
  }

  return (
    <span className="mr-1">
      <WandSparkles size={'24'} style={iconStyle} />
    </span>
  )
}

export interface ImageGenToolProps {
  prompt?: string
  imageUrl?: string
  error?: string
  isLoading?: boolean
}

export const ImageGenTool = ({ prompt, imageUrl, error, isLoading = true }: ImageGenToolProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [messageIndex, setMessageIndex] = useState(0)
  const [elapsedTime, setElapsedTime] = useState(0)
  const MAX_TIME = 60

  // Array of fun loading messages
  const loadingMessages = [
    'Baking your image...',
    'Mixing colors and pixels...',
    'Teaching AI to draw properly...',
    'Searching for inspiration...',
    'Asking digital artists for advice...',
    'Adding a touch of magic...',
    'Wrangling stray pixels...',
    'Making sure it looks just right...',
    'Finding the perfect composition...',
    'Adding a splash of creativity...',
    'Almost there, putting on finishing touches...',
    'Attempting to channel Salvador Dalí...',
  ]

  useEffect(() => {
    if (!isLoading) return

    // Update the message every 5 seconds
    const messageInterval = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessages.length)
    }, 5000)

    // Update the elapsed time every second
    const timerInterval = setInterval(() => {
      setElapsedTime((prevTime) => {
        if (prevTime >= MAX_TIME - 1) {
          clearInterval(timerInterval)
          clearInterval(messageInterval)
          return MAX_TIME
        }
        return prevTime + 1
      })
    }, 1000)

    // Clean up intervals on unmount
    return () => {
      clearInterval(messageInterval)
      clearInterval(timerInterval)
    }
  }, [isLoading])

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  // If loading, show the loading state
  if (isLoading) {
    return (
      <div className="bg-primary flex w-full flex-col justify-center gap-2 rounded-lg p-2 shadow-md">
        <div className="flex items-center justify-between gap-2 p-2">
          <div className="text-primary flex items-center gap-2">
            <RainbowMagicpen />
            <span className="flex flex-col">
              <span>{loadingMessages[messageIndex]}</span>
              <span className="text-xs opacity-70">Elapsed time: {formatTime(elapsedTime)}</span>
            </span>
          </div>
          {prompt && (
            <Button variant="ghost-neutral" size="padded-icon" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <CircleArrowDown size={'24'} className={cn(dropdownOpen && 'rotate-180')} />
            </Button>
          )}
        </div>
        {dropdownOpen && (
          <div className="p-1">
            <p>Prompt: {prompt}</p>
          </div>
        )}
      </div>
    )
  }

  // If there's an error, show the error state
  if (error) {
    return (
      <div className="bg-primary flex w-full flex-col justify-center gap-2 rounded-lg p-2 shadow-md">
        <div className="flex items-center justify-between gap-2 p-2">
          <div className="text-primary flex items-center gap-2">
            <Info size={24} className="text-red-80" />
            <span>Error generating image: {error}</span>
          </div>
        </div>
      </div>
    )
  }

  // If success, show the image
  return (
    <div className="bg-primary flex w-full flex-col justify-center gap-2 rounded-lg p-2 shadow-md">
      <div className="flex items-center justify-between gap-2 p-2">
        <div className="text-primary flex items-center gap-2">
          <span className="mr-1">
            <CheckCircle size={24} />
          </span>
          <span>Image generated successfully!</span>
        </div>
      </div>
    </div>
  )
}
